import { Flex, Image, ConnectionItem, Button } from "@wayflyer/flyui";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { type IntlShape, useIntl } from "react-intl";
import { useSearchParams } from "react-router";
import { connectByPath } from "../../data-sources";
import type { ConnectionPropertiesSchema, ConnectorPropertiesSchema } from "../../data-sources/schemas";

import { COMMON_MESSAGES } from "../common-messages";
import { getAutoInitiateReconnectPath } from "./get-auto-initiate-reconnect-path";

const connectionBadge = (
  connection: ConnectionPropertiesSchema,
  intl: IntlShape
): { badgeStatus: "positive" | "critical"; badgeText: string } =>
  connection.isConnected
    ? { badgeStatus: "positive", badgeText: intl.formatMessage(COMMON_MESSAGES.connected) }
    : {
        badgeStatus: "critical",
        badgeText: connection.reconnectPath
          ? intl.formatMessage(COMMON_MESSAGES.notConnected)
          : intl.formatMessage(COMMON_MESSAGES.reconnectNeeded),
      };

export const ConnectionsList = ({
  connectorProperties,
  connectForm,
}: {
  connectorProperties: ConnectorPropertiesSchema;
  connectForm: ReactNode;
}) => {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const [isFormVisible, setFormVisible] = useState(false);
  const hasAutoInitiated = useRef(false);

  const connections = useMemo(() => connectorProperties?.connections ?? [], [connectorProperties]);
  const hasOauthError = searchParams.has("oauth-error");

  useEffect(() => {
    const autoInitiatePath = getAutoInitiateReconnectPath(connections, hasOauthError);
    if (!hasAutoInitiated.current && autoInitiatePath) {
      hasAutoInitiated.current = true;
      connectByPath(autoInitiatePath);
    }
  }, [connections, hasOauthError]);

  return (
    <Flex direction="column" width="100%" gap="4">
      {connections.length > 0 ? (
        <>
          <Flex direction="column" gap="2">
            {connections.map((connection) => (
              <ConnectionItem
                key={connection.id}
                label={connection.identifierName ?? connectorProperties.name}
                logo={
                  connectorProperties.connectionLogoSrc ? (
                    <Image src={connectorProperties.connectionLogoSrc} alt="" />
                  ) : undefined
                }
                {...connectionBadge(connection, intl)}
                {...(!connection.isConnected &&
                  connection.reconnectPath && {
                    actions: [
                      {
                        content: intl.formatMessage(COMMON_MESSAGES.reconnectAction),
                        variant: "Tertiary",
                        onAction: () => {
                          connectByPath(connection.reconnectPath as string);
                        },
                      },
                    ],
                  })}
              />
            ))}
          </Flex>

          {!isFormVisible && connectorProperties.multipleConnectionsAllowed && (
            <Flex>
              <Button variant="Secondary" size="small" onClick={() => setFormVisible(true)}>
                {intl.formatMessage(COMMON_MESSAGES.addAnother)}
              </Button>
            </Flex>
          )}

          {isFormVisible && (
            <Flex direction="column" padding={["4", "0"]} gap="2">
              {connectForm}
            </Flex>
          )}
        </>
      ) : (
        connectForm
      )}
    </Flex>
  );
};
